/*
 * bookingDrawer — the modal around the Cliniko embed. Level 2, CSP build.
 *
 * Persistent chrome: the partial renders OUTSIDE `#swup`, so this initialises once at
 * boot and is never torn down. See components-klc.md §6.
 *
 * THE DELEGATED LISTENER IS THE POINT OF THE FILE. Seven of the nine [data-book] surfaces
 * live inside `#swup` and are replaced wholesale on every soft navigation, so a
 * per-trigger `@click` binds to elements that stop existing. One listener on `document`,
 * registered once, matching `event.target.closest('[data-book]')`, survives every DOM
 * replacement — a different mechanism from re-acquiring a node, and the one this problem
 * actually needs. The regression only appears AFTER a content swap, which is why the e2e
 * spec for it has to navigate first.
 *
 * WHY THE `src` IS KEYED RATHER THAN LOADED ONCE. "Promote on first open" is correct
 * until deep links exist, and then it silently serves the wrong appointment type: open
 * from /acupuncture/, close, soft-navigate to /cupping/, open again — the frame still
 * shows acupuncture, because it was already loaded. Storing the last-promoted URL and
 * re-assigning only when the requested URL differs keeps one request per distinct
 * treatment and still costs zero on page view.
 *
 * DEEP LINKS ARE ADDITIVE OR ABSENT. A trigger may carry `data-book-appointment-type`;
 * its value comes from the TREATMENT entry, never from a shared price row (an appointment
 * type is treatment × duration). The embedded view's query surface is not contractual, so
 * an unusable value must fall back to the bare URL unconditionally and silently. Until
 * the ids are verified against business_id 28227 nothing sets the attribute and every
 * button is bare-URL — the code path exists so that adding one is a content change.
 *
 * WHAT THIS DOES NOT OWN: the postMessage channel. That belongs to clinikoEmbed, because
 * both instances need it and only one of them is this drawer.
 *
 * Registered in resources/js/app.js as: Alpine.data('bookingDrawer', bookingDrawer)
 */

/** Appointment-type ids are Cliniko integers; anything else is not a deep link. */
const APPOINTMENT_TYPE_PATTERN = /^\d+$/;

export default function bookingDrawer() {
    return {
        open: false,

        /** @type {HTMLElement|null} The element that opened the drawer, to return focus to. */
        lastTrigger: null,

        /** @type {HTMLIFrameElement|null} */
        frame: null,

        /** @type {string|null} The URL currently loaded into the frame. */
        loadedUrl: null,

        /** @type {((event: MouseEvent) => void)|null} */
        clickHandler: null,

        init() {
            this.frame = this.$root.querySelector('[data-cliniko-frame]');

            // ONE listener, on document, for the lifetime of the page. Bound and stored so
            // destroy() can remove exactly this one — the drawer is never destroyed in
            // practice, but a component that leaks a document listener when it is teaches
            // the next component to do the same.
            this.clickHandler = (event) => this.onDocumentClick(event);
            document.addEventListener('click', this.clickHandler);
        },

        /**
         * @param {MouseEvent} event
         */
        onDocumentClick(event) {
            const trigger = event.target.closest?.('[data-book]');

            if (!trigger) {
                return;
            }

            // Let modified clicks through to the browser: a middle-click or ⌘-click on a
            // Book link should open /contact/#book in a tab, which is a reasonable thing
            // to want and is exactly what the real href is for.
            if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) {
                return;
            }

            event.preventDefault();
            this.openFor(trigger);
        },

        /**
         * @param {HTMLElement} trigger
         */
        openFor(trigger) {
            this.lastTrigger = trigger;
            this.promote(trigger.getAttribute('data-book-appointment-type'));
            this.open = true;

            // Focus the close button, NOT the iframe: focus placed inside a cross-origin
            // frame cannot be moved back out by us, which strands a keyboard user.
            // Deferred so the element is visible — x-show has not run yet at this point.
            this.$nextTick(() => {
                this.$root.querySelector('[data-booking-drawer-close]')?.focus();
            });
        },

        /**
         * Load the frame, or re-load it when a different appointment type is requested.
         *
         * @param {string|null} appointmentType
         */
        promote(appointmentType) {
            if (!this.frame) {
                // The placeholder state — booking_embed_url is blank. The panel already
                // renders a phone number and an enquiry link; there is nothing to load.
                return;
            }

            const base = this.frame.getAttribute('data-src');

            if (!base) {
                return;
            }

            const url = this.urlFor(base, appointmentType);

            if (url === this.loadedUrl) {
                return;
            }

            this.frame.setAttribute('src', url);
            this.loadedUrl = url;
        },

        /**
         * @param {string} base
         * @param {string|null} appointmentType
         * @return {string}
         */
        urlFor(base, appointmentType) {
            if (!appointmentType || !APPOINTMENT_TYPE_PATTERN.test(appointmentType)) {
                return base;
            }

            // Silent, unconditional fallback: an unparseable base must still produce a
            // working booking URL rather than an exception on the primary CTA.
            try {
                const url = new URL(base, window.location.href);
                url.searchParams.set('appointment_type_id', appointmentType);

                return url.toString();
            } catch {
                return base;
            }
        },

        close() {
            if (!this.open) {
                return;
            }

            this.open = false;

            // Focus returns to whatever opened the drawer. Stored explicitly on open,
            // because the delegated listener means we never had it as `$el`.
            //
            // DEFERRED, AND THAT IS NOT A TIDINESS CHOICE. While the drawer is open the
            // trigger sits inside a subtree x-trap has marked `inert`, and focusing an
            // inert element silently does nothing — focus lands on <body> instead, which
            // is exactly where a keyboard user does not want to be after closing a modal.
            // One tick later the trap has released and the call takes.
            const trigger = this.lastTrigger;
            this.lastTrigger = null;

            this.$nextTick(() => trigger?.focus?.());
        },

        destroy() {
            if (this.clickHandler) {
                document.removeEventListener('click', this.clickHandler);
                this.clickHandler = null;
            }

            this.frame = null;
            this.lastTrigger = null;
        },
    };
}
